'use client';

import { useEffect } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormControl, FormItem } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { BanknoteIcon, CreditCardIcon, WalletIcon } from 'lucide-react';

export type FormPaymentMethodType = { type?: string };

type PropsType = {
  values: string;
  isLoading?: boolean;
  onChange?: (val: string) => void;
  isPaidOff?: boolean;
  paymentType?: string;
  disabled?: boolean;
  isCodAvailable?: boolean;
  isDropship?: boolean;
};

export default function CardPaymentMethod({
  values,
  isLoading = false,
  onChange,
  isPaidOff = false,
  paymentType,
  disabled = false,
  isCodAvailable = false,
  isDropship = false,
}: PropsType) {
  const renderPaymentMethodLabel = () => {
    if (values === 'online') return 'Transfer Online';
    if (values === 'cod') return 'COD (Cash on Delivery)';
    return values;
  };

  // ✅ Automatically force "online" when dropship is active
  useEffect(() => {
    if (isDropship && values !== 'online') {
      onChange?.('online');
    }
  }, [isDropship, values, onChange]);

  return (
    <div className='mb-3'>
      <div className='flex items-center gap-x-2 pb-3'>
        <WalletIcon className='mt-0.5 h-5 w-5' />
        <h2 className='text-lg md:text-xl'>Payment Method</h2>
      </div>

      {isPaidOff ? (
        <span className='text-sm'>
          {`${renderPaymentMethodLabel()}${paymentType ? ` - ${paymentType}` : ''}`}
        </span>
      ) : (
        <FormControl>
          <RadioGroup
            key={values}
            defaultValue={values}
            onValueChange={onChange}
            className='space-y-2 pt-4'
            disabled={disabled || isLoading}
          >
            {/* Transfer Online */}
            <FormItem className='flex items-center justify-between space-x-3 space-y-0 border-b pb-4'>
              <div
                className={cn(
                  'flex cursor-pointer items-center gap-x-1',
                  isLoading && 'cursor-not-allowed opacity-50'
                )}
              >
                <CreditCardIcon className='mt-0.5 h-5 w-5' />
                Transfer Online
              </div>
              <FormControl>
                <RadioGroupItem value='online' />
              </FormControl>
            </FormItem>

            {/* COD option (hidden if dropship) */}
            {!isDropship && (
              <FormItem className='flex items-center justify-between space-x-3 space-y-0 border-b pb-4'>
                <div
                  className={cn(
                    'flex cursor-pointer items-center gap-x-1',
                    (isLoading || disabled || !isCodAvailable) && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <BanknoteIcon className='mt-0.5 h-5 w-5' />
                  COD (Cash on Delivery)
                </div>
                <FormControl>
                  <RadioGroupItem value='cod' disabled={!isCodAvailable || disabled} />
                </FormControl>
              </FormItem>
            )}
          </RadioGroup>
        </FormControl>
      )}
    </div>
  );
}
